import { createContext, useContext, useEffect, useReducer } from 'react';
import { useLocation } from 'react-router-dom';
import produce from 'immer';

import { useInsightsContext } from './InsightsContext';
import { genRandomString, objectToToken, tokenToObject, unimplemented } from '@utils/commonUtils';
import {
  AQLQuery,
  AQLQueryCondition,
  AQLQueryType,
  AQLQueryValue,
} from '@insights_module/adc_ql/types';
import { CategoryType, IabBrandSafetyCategoryType } from '@insights_module/types/BrandSafety';
import { AQLFlatQueryBuilder } from '@insights_module/adc_ql/QueryBuilder';
import {
  buildQueryFromQueryData,
  createQueryDataFromQuery,
  groupQueryDataByGroupId,
} from '@insights_module/adc_ql/utils';
import { useInsightsParams } from '@insights_module/hooks/useInsightsParams';
import { EPISODE_FILTER_TYPES, SHOW_FILTER_TYPES } from '@insights_module/adc_ql/constants';

export type SegmentCreatorData = {
  id: string;
  groupId: string | null;
  type: AQLQueryType;
  negation: boolean | null;
  category?:
    | CategoryType
    | 'all-garm-categories'
    | IabBrandSafetyCategoryType
    | 'all-iab-categories'
    | null;
  condition?: AQLQueryCondition | null;
  operator?: 'eq' | 'wildcard' | null;
  value?: AQLQueryValue | null;
};

export const ROOT_SEGMENT_ID = 'root-segment-id';
export const ALL_GARM_CATEGORIES = 'all-garm-categories';
export const ALL_IAB_CATEGORIES = 'all-iab-categories';

type SegmentCreatorKeys =
  | 'insights.segment_creator.set_segments_data'
  | 'insights.segment_creator.push_segment_data'
  | 'insights.segment_creator.remove_segment_data'
  | 'insights.segment_creator.remove_group_segment_data'
  | 'insights.segment_creator.clone_segment_data'
  | 'insights.segment_creator.insert_segment_data'
  | 'insights.segment_creator.total_episode_count'
  | 'insights.segment_creator.is_total_episode_count_loading'
  | 'insights.segment_creator.is_presentational'
  | 'insights.segment_creator.query'
  | 'insights.segment_creator.query_token'
  | 'insights.segment_creator.query_type';

const SEGMENT_CREATOR_KEYS = {
  SET_SEGMENTS_DATA: 'insights.segment_creator.set_segments_data' as SegmentCreatorKeys,
  PUSH_SEGMENT_DATA: 'insights.segment_creator.push_segment_data' as SegmentCreatorKeys,
  INSERT_SEGMENT_DATA: 'insights.segment_creator.insert_segment_data' as SegmentCreatorKeys,
  REMOVE_SEGMENT_DATA: 'insights.segment_creator.remove_segment_data' as SegmentCreatorKeys,
  REMOVE_GROUP_SEGMENT_DATA:
    'insights.segment_creator.remove_group_segment_data' as SegmentCreatorKeys,
  CLONE_SEGMENT_DATA: 'insights.segment_creator.clone_segment_data' as SegmentCreatorKeys,
  TOTAL_EPISODE_COUNT: 'insights.segment_creator.total_episode_count' as SegmentCreatorKeys,
  IS_TOTAL_EPISODE_COUNT_LOADING:
    'insights.segment_creator.is_total_episode_count_loading' as SegmentCreatorKeys,
  IS_PRESENTATIONAL: 'insights.segment_creator.is_presentational' as SegmentCreatorKeys,
  QUERY: 'insights.segment_creator.query' as SegmentCreatorKeys,
  QUERY_TOKEN: 'insights.segment_creator.query_token' as SegmentCreatorKeys,
  QUERY_TYPE: 'insights.segment_creator.query_type' as SegmentCreatorKeys,
};

type SegmentCreatorInsertPayload = {
  data: SegmentCreatorData | SegmentCreatorData[];
  index: number;
};

type Action = {
  type: SegmentCreatorKeys;
  payload:
    | string
    | number
    | boolean
    | SegmentCreatorData
    | SegmentCreatorData[]
    | JSX.Element
    | JSX.Element[]
    | SegmentCreatorInsertPayload
    | AQLQuery;
};

export interface ISegmentCreatorContext {
  segmentsData: SegmentCreatorData[];
  totalEpisodeCount: number;
  isPresentational: boolean;
  isTotalEpisodesCountLoading: boolean;
  query: AQLQuery | null;
  queryToken: string | null;
  queryType: 'audio' | 'group';
  setSegmentsData: (data: SegmentCreatorData[]) => void;
  pushSegmentData: (data: SegmentCreatorData) => void;
  insertSegmentData: (data: SegmentCreatorData | SegmentCreatorData[], index: number) => void;
  removeSegmentData: (segmentId: string) => void;
  removeGroupSegmentData: (groupId: string) => void;
  cloneSegmentData: (segmentId: string) => void;
  setTotalEpisodeCount: (count: number) => void;
  getSegmentData: (segmentId: string) => SegmentCreatorData | undefined;
  getElementIndex: (segmentId: string) => number;
  setIsPresentational: (isPresentational: boolean) => void;
  setIsTotalEpisodesLoading: (val: boolean) => void;
  setQueryType: (type: 'audio' | 'group') => void;
}

const initialState: ISegmentCreatorContext = {
  segmentsData: [],
  totalEpisodeCount: 0,
  isPresentational: false,
  isTotalEpisodesCountLoading: false,
  query: null,
  queryToken: null,
  queryType: 'audio',
  setSegmentsData: unimplemented,
  pushSegmentData: unimplemented,
  insertSegmentData: unimplemented,
  removeSegmentData: unimplemented,
  removeGroupSegmentData: unimplemented,
  cloneSegmentData: unimplemented,
  setTotalEpisodeCount: unimplemented,
  setIsPresentational: unimplemented,
  getSegmentData: () => undefined,
  getElementIndex: () => -1,
  setIsTotalEpisodesLoading: unimplemented,
  setQueryType: unimplemented,
};

const reducer = produce((draft: ISegmentCreatorContext, action: Action) => {
  const { type, payload } = action;
  switch (type) {
    case SEGMENT_CREATOR_KEYS.SET_SEGMENTS_DATA:
      draft.segmentsData = payload as SegmentCreatorData[];
      break;

    case SEGMENT_CREATOR_KEYS.PUSH_SEGMENT_DATA: {
      const newSegment = payload as SegmentCreatorData;
      const existingSegment = draft.segmentsData.find((segment) => segment.id === newSegment.id);
      if (existingSegment) {
        Object.assign(existingSegment, newSegment);
      } else {
        draft.segmentsData.push(newSegment);
      }

      break;
    }

    case SEGMENT_CREATOR_KEYS.INSERT_SEGMENT_DATA: {
      const { data, index } = payload as SegmentCreatorInsertPayload;
      draft.segmentsData.splice(index, 0, ...(Array.isArray(data) ? data : [data]));
      break;
    }

    case SEGMENT_CREATOR_KEYS.REMOVE_SEGMENT_DATA: {
      const segmentId = payload as string;
      draft.segmentsData = draft.segmentsData.filter((segment) => segment.id !== segmentId);

      break;
    }

    case SEGMENT_CREATOR_KEYS.REMOVE_GROUP_SEGMENT_DATA: {
      const groupId = payload as string;
      draft.segmentsData = draft.segmentsData.filter(
        (segment) => segment.groupId !== groupId && segment.id !== groupId
      );

      break;
    }

    case SEGMENT_CREATOR_KEYS.CLONE_SEGMENT_DATA: {
      const segmentId = payload as string;
      const segmentIndex = draft.segmentsData.findIndex(
        (segmentsData) => segmentsData.id === segmentId
      );
      if (segmentIndex !== -1) {
        const clonedSegment = { ...draft.segmentsData[segmentIndex], id: genRandomString(16) };
        draft.segmentsData.splice(segmentIndex + 1, 0, clonedSegment);
      }

      break;
    }

    case SEGMENT_CREATOR_KEYS.TOTAL_EPISODE_COUNT:
      draft.totalEpisodeCount = payload as number;
      break;

    case SEGMENT_CREATOR_KEYS.IS_PRESENTATIONAL:
      draft.isPresentational = payload as boolean;
      break;

    case SEGMENT_CREATOR_KEYS.IS_TOTAL_EPISODE_COUNT_LOADING:
      draft.isTotalEpisodesCountLoading = payload as boolean;
      break;

    case SEGMENT_CREATOR_KEYS.QUERY:
      draft.query = payload as AQLQuery | null;
      break;

    case SEGMENT_CREATOR_KEYS.QUERY_TOKEN:
      draft.queryToken = payload as string | null;
      break;

    case SEGMENT_CREATOR_KEYS.QUERY_TYPE:
      draft.queryType = payload as 'audio' | 'group';
      break;

    default:
      break;
  }
});

export const SegmentCreatorContext = createContext<ISegmentCreatorContext>(initialState);

export const SegmentCreatorProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { segmentsData } = state;
  const { queryToken, setQueryToken } = useInsightsParams();
  const { selectedTableType, setSelectedTableType } = useInsightsContext();
  const { pathname } = useLocation();

  useEffect(() => {
    const paths = pathname.split('/');
    if (paths.includes('integrations')) return;

    if (selectedTableType === 'shows') {
      dispatch({ type: SEGMENT_CREATOR_KEYS.QUERY_TYPE, payload: 'group' });
    } else {
      dispatch({ type: SEGMENT_CREATOR_KEYS.QUERY_TYPE, payload: 'audio' });
    }

    // if filter type and selected table type are not compatible, reset query token
    if (
      (segmentsData.find((segmentData) => EPISODE_FILTER_TYPES.includes(segmentData.type)) &&
        selectedTableType === 'shows') ||
      (segmentsData.find((segmentData) => SHOW_FILTER_TYPES.includes(segmentData.type)) &&
        selectedTableType === 'episodes')
    ) {
      setQueryToken(null);
    }
  }, [selectedTableType]);

  useEffect(() => {
    if (queryToken) {
      const query = tokenToObject(queryToken as string);
      const segmentsData = createQueryDataFromQuery([query]);

      // if filter type and selected table type are not compatible, set table type to compatible one
      if (segmentsData.find((segmentData) => EPISODE_FILTER_TYPES.includes(segmentData.type))) {
        setSelectedTableType('episodes');
      }
      if (segmentsData.find((segmentData) => SHOW_FILTER_TYPES.includes(segmentData.type))) {
        setSelectedTableType('shows');
      }
      setSegmentsData(segmentsData);
    }
  }, [queryToken]);

  useEffect(() => {
    const builder = new AQLFlatQueryBuilder();
    buildQueryFromQueryData(groupQueryDataByGroupId(segmentsData), builder, null);
    const query = builder.build();
    dispatch({ type: SEGMENT_CREATOR_KEYS.QUERY, payload: query });

    if (!query) return;
    const token = objectToToken(query);
    dispatch({ type: SEGMENT_CREATOR_KEYS.QUERY_TOKEN, payload: token });
  }, [segmentsData]);

  const setSegmentsData = (data: SegmentCreatorData[]) => {
    dispatch({ type: SEGMENT_CREATOR_KEYS.SET_SEGMENTS_DATA, payload: data });
  };

  const pushSegmentData = (data: SegmentCreatorData) => {
    dispatch({ type: SEGMENT_CREATOR_KEYS.PUSH_SEGMENT_DATA, payload: data });
  };

  const insertSegmentData = (data: SegmentCreatorData | SegmentCreatorData[], index: number) => {
    dispatch({ type: SEGMENT_CREATOR_KEYS.INSERT_SEGMENT_DATA, payload: { data, index } });
  };

  const removeSegmentData = (segmentDataId: string) => {
    dispatch({ type: SEGMENT_CREATOR_KEYS.REMOVE_SEGMENT_DATA, payload: segmentDataId });
  };

  const removeGroupSegmentData = (groupId: string) => {
    dispatch({ type: SEGMENT_CREATOR_KEYS.REMOVE_GROUP_SEGMENT_DATA, payload: groupId });
  };

  const cloneSegmentData = (segmentDataId: string) => {
    dispatch({ type: SEGMENT_CREATOR_KEYS.CLONE_SEGMENT_DATA, payload: segmentDataId });
  };

  const setTotalEpisodeCount = (count: number) => {
    dispatch({ type: SEGMENT_CREATOR_KEYS.TOTAL_EPISODE_COUNT, payload: count });
  };

  const setIsPresentational = (isPresentational: boolean) => {
    dispatch({ type: SEGMENT_CREATOR_KEYS.IS_PRESENTATIONAL, payload: isPresentational });
  };

  const getSegmentData = (segmentDataId: string) => {
    return state.segmentsData.find((segmentData) => segmentData.id === segmentDataId);
  };

  const getElementIndex = (segmentDataId: string) => {
    return state.segmentsData.findIndex((segmentData) => segmentData.id === segmentDataId);
  };

  const setIsTotalEpisodesLoading = (payload: boolean) => {
    dispatch({ type: SEGMENT_CREATOR_KEYS.IS_TOTAL_EPISODE_COUNT_LOADING, payload: payload });
  };

  const setQueryType = (payload: 'audio' | 'group') => {
    dispatch({ type: SEGMENT_CREATOR_KEYS.QUERY_TYPE, payload: payload });
  };

  const value = {
    ...state,
    setSegmentsData,
    pushSegmentData,
    insertSegmentData,
    removeSegmentData,
    removeGroupSegmentData,
    cloneSegmentData,
    setTotalEpisodeCount,
    setIsPresentational,
    getSegmentData,
    getElementIndex,
    setIsTotalEpisodesLoading,
    setQueryType,
  };

  return <SegmentCreatorContext.Provider value={value}>{children}</SegmentCreatorContext.Provider>;
};

export const useSegmentCreatorContext = () => useContext(SegmentCreatorContext);
